import React, { useEffect, useMemo, useState } from "react";
import {
  Plus,
  RefreshCw,
  Users,
  Globe2,
  Bug,
  GripVertical,
  LayoutDashboard,
  SlidersHorizontal,
  Pencil,
  PauseCircle,
  PlayCircle,
  Trash2,
  Download,
  DatabaseBackup,
  FileSpreadsheet,
  Eye,
  EyeOff,
  Languages,
} from "lucide-react";
import { api, downloadDiagnostics } from "../lib/api.js";
import { Card, Pill, Modal, Empty, Notice } from "../components/UI.jsx";
import PeriodFilter, { resolvePeriod } from "../components/PeriodFilter.jsx";
import dictionaries, { t, supportedLocales } from "../lib/i18n.js";
import { uiTranslationRows } from "../lib/uiPhraseTranslations.js";

const defaultOrder = [
  "currency",
  "display",
  "header",
  "localization",
  "export",
  "users",
  "debug",
];
const widgetOptions = [
  ["cash_balance", "Cash balance"],
  ["net_worth", "Net worth"],
  ["passive_ytd", "Passive income YTD"],
  ["account_count", "Account count"],
  ["fx_pair", "Selected FX pair"],
];
const headerScreens = [
  ["dashboard", "Dashboard"],
  ["accounts", "Accounts"],
  ["account_detail", "Account detail"],
  ["movements", "Movements"],
  ["import", "Import"],
  ["organize", "Organize"],
  ["rules", "Rules"],
  ["planning", "Budgets, goals & projects"],
  ["subscriptions", "Recurring payments"],
  ["investments", "Investments"],
  ["config", "Configuration"],
  ["audit", "Audit"],
  ["profile", "Profile"],
];

export default function Config({ user, onDebugChange }) {
  const [cfg, setCfg] = useState(null),
    [fx, setFx] = useState(null),
    [accounts, setAccounts] = useState([]),
    [users, setUsers] = useState([]),
    [version, setVersion] = useState(""),
    [clientErrors, setClientErrors] = useState([]),
    [modal, setModal] = useState(null),
    [msg, setMsg] = useState(""),
    [error, setError] = useState(""),
    [signupCode, setSignupCode] = useState(""),
    [dragKey, setDragKey] = useState(null),
    [headerScreen, setHeaderScreen] = useState("dashboard"),
    [editingUser, setEditingUser] = useState(null),
    [userForm, setUserForm] = useState({
      name: "",
      email: "",
      password: "",
      role: "user",
      isActive: true,
    }),
    [exportForm, setExportForm] = useState({
      accountIds: [],
      dateFrom: "",
      dateTo: "",
      format: "pisto",
      includeDefinitions: true,
      backupMode: "private",
      sharingKey: "",
    }),
    [exportPeriod, setExportPeriod] = useState(() => ({
      mode: "all",
      ...resolvePeriod("all"),
      years: [],
      months: [],
    })),
    [exportBusy, setExportBusy] = useState(false);
  const [localizationRows, setLocalizationRows] = useState([]),
    [editEnglish, setEditEnglish] = useState(false),
    [translationSearch, setTranslationSearch] = useState("");

  const isAdmin = ["super_admin", "admin"].includes(user.role);
  const load = () => {
    setError("");
    Promise.all([
      api("/config"),
      api("/fx/latest").catch(() => null),
      api("/version").catch(() => ({ version: "" })),
      api("/accounts").catch(() => []),
    ])
      .then(([c, r, v, a]) => {
        setCfg(c);
        setFx(r);
        setVersion(v.version || "");
        setAccounts(a);
        setExportForm((x) => ({
          ...x,
          accountIds: x.accountIds.length
            ? x.accountIds
            : a.map((y) => Number(y.id)),
        }));
      })
      .catch((e) => setError(e.message));
    if (isAdmin) {
      api("/users")
        .then(setUsers)
        .catch(() => {});
      api("/client-errors")
        .then(setClientErrors)
        .catch(() => {});
      api("/localization")
        .then((saved) => {
          const byKey = Object.fromEntries(saved.map((x) => [x.string_key, x]));
          const builtInRows=Object.keys(dictionaries.en).sort().map((key) => ({
              key,
              en: byKey[key]?.en_text || dictionaries.en[key] || "",
              es: byKey[key]?.es_text || dictionaries.es[key] || "",
              pt: byKey[key]?.pt_text || dictionaries.pt[key] || "",
            }));
          const phraseRows=uiTranslationRows.map(row=>({key:row.key,en:byKey[row.key]?.en_text||row.en,es:byKey[row.key]?.es_text||row.es,pt:byKey[row.key]?.pt_text||row.pt}));
          setLocalizationRows([...builtInRows,...phraseRows].sort((a,b)=>a.en.localeCompare(b.en)));
        })
        .catch(() => {});
    }
  };
  useEffect(() => {
    load();
  }, []);

  const prefs = cfg?.preferences || {};
  const order = useMemo(() => {
    try {
      const x =
        typeof prefs.config_layout_json === "string"
          ? JSON.parse(prefs.config_layout_json)
          : prefs.config_layout_json;
      const base =
        Array.isArray(x) && x.length
          ? x.filter((k) => defaultOrder.includes(k))
          : [];
      return [...base, ...defaultOrder.filter((k) => !base.includes(k))];
    } catch {
      return defaultOrder;
    }
  }, [prefs.config_layout_json]);
  const headerMap = (() => {
    try {
      return typeof prefs.header_widgets_json === "string"
        ? JSON.parse(prefs.header_widgets_json || "{}")
        : prefs.header_widgets_json || {};
    } catch {
      return {};
    }
  })();

  const savePrefs = async (patch) => {
    try {
      const next = {
        hideValues: Boolean(prefs.hide_values_default),
        maxDashboardTabs: Number(prefs.max_dashboard_tabs || 5),
        debugEnabled: Boolean(prefs.debug_enabled),
        headerWidgets: headerMap,
        configLayout: order,
        reportCurrency: cfg.userBaseCurrency || "EUR",
        fxBaseCurrency: prefs.fx_base_currency || "EUR",
        fxQuoteCurrency: prefs.fx_quote_currency || "USD",
        currencyDisplayMode: prefs.currency_display_mode || "symbol",
        ...patch,
      };
      await api("/preferences", { method: "PUT", body: next });
      localStorage.setItem(
        "pisto-debug-enabled",
        next.debugEnabled ? "1" : "0",
      );
      localStorage.setItem(
        "pisto-hide-values-runtime",
        next.hideValues ? "1" : "0",
      );
      localStorage.setItem("pisto-currency-display-mode", next.currencyDisplayMode);
      onDebugChange?.(Boolean(next.debugEnabled));
      await load();
    } catch (e) {
      setError(e.message);
    }
  };
  const moveCard = (from, to) => {
    if (!from || from === to) return;
    const next = [...order],
      i = next.indexOf(from),
      j = next.indexOf(to);
    if (i < 0 || j < 0) return;
    next.splice(i, 1);
    next.splice(j, 0, from);
    savePrefs({ configLayout: next });
  };
  const setHeaderWidget = (index, value) => {
    const selected = [...(headerMap[headerScreen] || [])];
    if (value) selected[index] = value;
    else selected.splice(index, 1);
    savePrefs({
      headerWidgets: {
        ...headerMap,
        [headerScreen]: selected.filter(Boolean).slice(0, 3),
      },
    });
  };
  const syncFx = async () => {
    try {
      const r = await api("/fx/sync", { method: "POST", body: {} });
      setMsg(`ECB exchange rates updated for ${r.date}.`);
      load();
    } catch (e) {
      setError(e.message);
    }
  };
  const saveLocalization = async () => {
    try {
      await api("/localization", {
        method: "PUT",
        body: { rows: localizationRows },
      });
      setMsg("Translations saved. Refresh Pisto to load the new wording.");
      setModal(null);
    } catch (e) {
      setError(e.message);
    }
  };
  const saveSignup = async () => {
    try {
      await api("/admin/settings", { method: "PUT", body: { signupCode } });
      setSignupCode("");
      setMsg("Account creation code updated.");
    } catch (e) {
      setError(e.message);
    }
  };

  const openUser = (x) => {
    setEditingUser(x || null);
    setUserForm(
      x
        ? {
            name: x.name,
            email: x.email,
            password: "",
            role: x.role,
            isActive: !!x.is_active,
          }
        : { name: "", email: "", password: "", role: "user", isActive: true },
    );
    setModal("user");
  };
  const saveUser = async (e) => {
    e.preventDefault();
    try {
      if (editingUser)
        await api("/users/" + editingUser.id, {
          method: "PUT",
          body: userForm,
        });
      else await api("/users", { method: "POST", body: userForm });
      setModal(null);
      setEditingUser(null);
      load();
      setMsg(editingUser ? "User updated." : "User created.");
    } catch (e) {
      setError(e.message);
    }
  };
  const setUserActive = async (x) => {
    const active = !x.is_active;
    if (!confirm(`${active ? "Restore" : "Suspend"} access for ${x.name}?`))
      return;
    try {
      await api("/users/" + x.id, {
        method: "PUT",
        body: { name: x.name, email: x.email, role: x.role, isActive: active },
      });
      load();
    } catch (e) {
      setError(e.message);
    }
  };
  const deleteUser = async (x) => {
    if (
      !confirm(
        `Permanently delete ${x.name}? Pisto will refuse if this user still owns financial accounts.`,
      )
    )
      return;
    try {
      await api("/users/" + x.id, { method: "DELETE" });
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  const toggleExportAccount = (id) =>
    setExportForm((x) => ({
      ...x,
      accountIds: x.accountIds.includes(id)
        ? x.accountIds.filter((v) => v !== id)
        : [...x.accountIds, id],
    }));
  const downloadExport = async () => {
    try {
      if (!exportForm.accountIds.length)
        throw new Error("Choose at least one account.");
      setExportBusy(true);
      setError("");
      const r = await fetch("/api/export", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(exportForm),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.error || "Export failed.");
      }
      const blob = await r.blob(),
        cd = r.headers.get("content-disposition") || "",
        m = cd.match(/filename="([^"]+)"/),
        name =
          m?.[1] ||
          (exportForm.format === "csv"
            ? "pisto-movements.csv"
            : "pisto-backup.pisto");
      const url = URL.createObjectURL(blob),
        a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      setMsg(`Export created: ${name}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setExportBusy(false);
    }
  };

  if (!cfg) return <div className="loading">Loading configuration…</div>;
  const currencies = ["EUR", ...(fx?.rates || []).map((r) => r.currency)]
    .filter((x, i, a) => a.indexOf(x) === i)
    .sort();
  const head = (key, icon, title, action = null) => (
    <div className="section-head config-head">
      <div>
        {icon}
        <h2>{title}</h2>
      </div>
      <div className="config-head-actions">
        {action}
        <span
          draggable
          title="Drag to reorder this configuration card"
          className="inline-drag"
          onDragStart={(e) => {
            e.stopPropagation();
            setDragKey(key);
          }}
        >
          <GripVertical size={17} />
        </span>
      </div>
    </div>
  );
  const localClientError = (() => {
      try {
        return JSON.parse(
          localStorage.getItem("pisto-last-client-error") || "null",
        );
      } catch {
        return null;
      }
    })(),
    lastClientError = clientErrors[0] || localClientError;
  const cards = {
    currency: (
      <Card>
        {head(
          "currency",
          <Globe2 />,
          "Currency & locale",
          <button className="secondary compact" onClick={syncFx}>
            <RefreshCw size={14} /> Update FX
          </button>,
        )}
        <div className="fx-settings-grid">
          <label>
            {t(user.locale, "preferredLanguage")}
            <select
              value={String(user.locale || "en").slice(0, 2)}
              onChange={async (e) => {
                await api("/preferences/language", {
                  method: "PUT",
                  body: { locale: e.target.value },
                });
                window.location.reload();
              }}
            >
              {supportedLocales.map(([code, name]) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
            <small>The page refreshes to apply every translated string.</small>
          </label>
          <label>
            Reporting currency
            <select
              value={cfg.userBaseCurrency || "EUR"}
              onChange={(e) => savePrefs({ reportCurrency: e.target.value })}
            >
              {currencies.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
            <small>Used for combined totals across account currencies.</small>
          </label>
          <div className="setting-readonly">
            <span>FX source</span>
            <b>ECB daily reference rates</b>
          </div>
          <label>
            Header FX base
            <select
              value={prefs.fx_base_currency || "EUR"}
              onChange={(e) => savePrefs({ fxBaseCurrency: e.target.value })}
            >
              {currencies.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
          <label>
            Header FX quote
            <select
              value={prefs.fx_quote_currency || "USD"}
              onChange={(e) => savePrefs({ fxQuoteCurrency: e.target.value })}
            >
              {currencies.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
        </div>
      </Card>
    ),
    display: (
      <Card>
        {head("display", <SlidersHorizontal />, "Display & dashboard")}
        <div className="settings-lines">
          <label
            className="check"
            title="When enabled, all money values start hidden and the preference persists between sessions."
          >
            <input
              type="checkbox"
              checked={Boolean(prefs.hide_values_default)}
              onChange={(e) => savePrefs({ hideValues: e.target.checked })}
            />
            <span>
              <b>Hide money values by default</b>
              <small>Displays ****.** until the eye icon is used.</small>
            </span>
          </label>
          <label>
            Maximum dashboard tabs
            <select
              value={prefs.max_dashboard_tabs || 5}
              onChange={(e) =>
                savePrefs({ maxDashboardTabs: Number(e.target.value) })
              }
            >
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>
          </label>
          <label>
            Currency labels
            <select value={prefs.currency_display_mode || "symbol"} onChange={(e) => savePrefs({ currencyDisplayMode: e.target.value })}>
              <option value="symbol">Symbols (€, US$, £)</option>
              <option value="code">ISO codes (EUR, USD, GBP)</option>
            </select>
            <small>Symbols are the default. ISO codes are clearer when currencies share the same symbol.</small>
          </label>
        </div>
      </Card>
    ),
    header: (
      <Card>
        {head("header", <LayoutDashboard />, "Header mini cards")}
        <p className="card-help">
          Choose up to three compact cards beside each screen title. They stack
          vertically on phones.
        </p>
        <div className="header-card-settings">
          <label>
            Screen
            <select
              value={headerScreen}
              onChange={(e) => setHeaderScreen(e.target.value)}
            >
              {headerScreens
                .filter(([v]) => v !== "audit" || isAdmin)
                .map(([v, n]) => (
                <option key={v} value={v}>
                  {n}
                </option>
                ))}
            </select>
          </label>
          <div className="mini-card-selectors">
            {[0, 1, 2].map((i) => (
              <label key={i}>
                Card {i + 1}
                <select
                  value={(headerMap[headerScreen] || [])[i] || ""}
                  onChange={(e) => setHeaderWidget(i, e.target.value)}
                >
                  <option value="">None</option>
                  {widgetOptions.map(([v, n]) => (
                    <option key={v} value={v}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        </div>
      </Card>
    ),
    localization: isAdmin ? (
      <Card>
        {head("localization", <Languages />, t(user.locale, "localization"))}
        <p className="card-help">{t(user.locale, "localizationHelp")}</p>
        <button className="secondary" onClick={() => setModal("localization")}>
          <Languages size={16} /> {t(user.locale, "edit")}
        </button>
      </Card>
    ) : null,
    export: (
      <Card>
        {head("export", <DatabaseBackup />, "Export & backup")}
        <p className="card-help">
          CSV is readable in Excel. Encrypted .pisto backups preserve movements
          and, optionally, classifications/configuration data for Pisto
          restoration.
        </p>
        <div className="export-settings">
          <div className="export-account-row">
            <button
              className={
                exportForm.accountIds.length === accounts.length
                  ? "filter-pill active"
                  : "filter-pill"
              }
              onClick={() =>
                setExportForm((x) => ({
                  ...x,
                  accountIds: accounts.map((a) => Number(a.id)),
                }))
              }
            >
              All accounts
            </button>
            {accounts.map((a) => (
              <button
                key={a.id}
                className={
                  exportForm.accountIds.includes(Number(a.id))
                    ? "filter-pill active"
                    : "filter-pill"
                }
                onClick={() => toggleExportAccount(Number(a.id))}
              >
                {a.name} · {a.currency}
              </button>
            ))}
          </div>
          <div className="form-grid compact-grid">
            <label>
              From
              <input
                type="date"
                value={exportForm.dateFrom}
                onChange={(e) =>
                  setExportForm({ ...exportForm, dateFrom: e.target.value })
                }
              />
            </label>
            <label>
              To
              <input
                type="date"
                value={exportForm.dateTo}
                onChange={(e) =>
                  setExportForm({ ...exportForm, dateTo: e.target.value })
                }
              />
            </label>
            <label>
              Export format
              <select
                value={exportForm.format}
                onChange={(e) =>
                  setExportForm({ ...exportForm, format: e.target.value })
                }
              >
                <option value="pisto">Encrypted Pisto backup (.pisto)</option>
                <option value="csv">CSV / Excel-readable</option>
              </select>
            </label>
            <label className="check">
              <input
                type="checkbox"
                disabled={exportForm.format === "csv"}
                checked={exportForm.includeDefinitions}
                onChange={(e) =>
                  setExportForm({
                    ...exportForm,
                    includeDefinitions: e.target.checked,
                  })
                }
              />
              <span>
                <b>Include classifications & planning data</b>
                <small>
                  Categories, tags, cost centers, places, projects, budgets,
                  goals, rules, subscriptions and assets.
                </small>
              </span>
            </label>
          </div>
          <button
            className="primary"
            onClick={downloadExport}
            disabled={exportBusy}
          >
            {exportForm.format === "csv" ? (
              <FileSpreadsheet size={16} />
            ) : (
              <Download size={16} />
            )}{" "}
            {exportBusy ? "Preparing…" : "Create export"}
          </button>
        </div>
      </Card>
    ),
    users: isAdmin ? (
      <Card>
        {head(
          "users",
          <Users />,
          "Users",
          <button
            className="icon"
            title="Create a user"
            onClick={() => openUser(null)}
          >
            <Plus />
          </button>,
        )}
        {!users.length ? (
          <Empty />
        ) : (
          <div className="simple-list user-admin-list">
            {users.map((x) => (
              <div key={x.id}>
                <span>
                  <b>{x.name}</b>
                  <small>{x.email}</small>
                </span>
                <span className="row-actions">
                  <Pill tone={x.is_active ? "green" : "red"}>
                    {x.is_active ? "Active" : "Suspended"}
                  </Pill>
                  <Pill>{x.role}</Pill>
                  <button
                    className="icon"
                    title="Edit user"
                    onClick={() => openUser(x)}
                  >
                    <Pencil size={15} />
                  </button>
                  {Number(x.id) !== Number(user.id) &&
                    x.role !== "super_admin" && (
                      <>
                        <button
                          className="icon"
                          title={
                            x.is_active ? "Suspend access" : "Restore access"
                          }
                          onClick={() => setUserActive(x)}
                        >
                          {x.is_active ? (
                            <PauseCircle size={15} />
                          ) : (
                            <PlayCircle size={15} />
                          )}
                        </button>
                        <button
                          className="icon danger-icon"
                          title="Delete user permanently"
                          onClick={() => deleteUser(x)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </>
                    )}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    ) : null,
    debug: isAdmin ? (
      <Card>
        {head("debug", <Bug />, "Admin & debug")}
        <div className="settings-lines">
          <label className="check">
            <input
              type="checkbox"
              checked={Boolean(prefs.debug_enabled)}
              onChange={(e) => savePrefs({ debugEnabled: e.target.checked })}
            />
            <span>
              <b>Show screen/dialog IDs</b>
              <small>
                IDs such as S0200 and D0201 make beta reports precise.
              </small>
            </span>
          </label>
          <label>
            New-account code
            <div className="inline-field">
              <input
                type="password"
                value={signupCode}
                onChange={(e) => setSignupCode(e.target.value)}
                placeholder="Set / replace invite code"
              />
              <button className="secondary" type="button" onClick={saveSignup}>
                Save
              </button>
            </div>
          </label>
          <div className="setting-readonly">
            <span>Locally queued outage reports</span>
            <b>{(()=>{try{return JSON.parse(localStorage.getItem("pisto-pending-diagnostics")||"[]").length}catch{return 0}})()} saved</b>
            <button type="button" className="secondary compact" onClick={downloadDiagnostics}>Download diagnostic report</button>
            <small>These survive a Hostinger 503 and upload automatically when Pisto becomes available again.</small>
          </div>
          {lastClientError && (
            <div className="debug-last-error">
              <b>Last browser error</b>
              <small>
                {lastClientError.created_at || lastClientError.at || ""}
                {lastClientError.path ? ` · ${lastClientError.path}` : ""}
              </small>
              <code>{lastClientError.message}</code>
              {(lastClientError.stack_text || lastClientError.stack) && (
                <details>
                  <summary>Technical stack</summary>
                  <code>
                    {lastClientError.stack_text || lastClientError.stack}
                  </code>
                </details>
              )}
              <button
                className="text-button"
                onClick={async () => {
                  localStorage.removeItem("pisto-last-client-error");
                  await api("/client-errors", { method: "DELETE" }).catch(
                    () => {},
                  );
                  setClientErrors([]);
                }}
              >
                Clear log
              </button>
            </div>
          )}
        </div>
      </Card>
    ) : null,
  };

  return (
    <>
      <div className="page-head config-page-head">
        <div>
          <h1>Configuration</h1>
          <p>
            Application defaults, display, backup, security and administrator
            controls.
          </p>
        </div>
        <span className="version-text">
          {version ? `Version ${version}` : ""}
        </span>
      </div>
      {msg && <div className="notice">{msg}</div>}
      {error && <div className="error">{error}</div>}
      <div className="config-grid">
        {order.map((k) =>
          cards[k] ? (
            <div
              className="config-card-wrap"
              key={k}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                moveCard(dragKey, k);
                setDragKey(null);
              }}
            >
              {cards[k]}
            </div>
          ) : null,
        )}
      </div>
      {modal === "user" && (
        <Modal
          title={editingUser ? "Edit user" : "Create user"}
          screenId="D0801"
          onClose={() => setModal(null)}
        >
          <form onSubmit={saveUser} className="form-grid">
            <label>
              Name
              <input
                required
                value={userForm.name}
                onChange={(e) =>
                  setUserForm({ ...userForm, name: e.target.value })
                }
              />
            </label>
            <label>
              Email
              <input
                required
                type="email"
                value={userForm.email}
                onChange={(e) =>
                  setUserForm({ ...userForm, email: e.target.value })
                }
              />
            </label>
            <label>
              {editingUser ? "New password (optional)" : "Temporary password"}
              <input
                type="password"
                minLength={10}
                required={!editingUser}
                value={userForm.password}
                onChange={(e) =>
                  setUserForm({ ...userForm, password: e.target.value })
                }
              />
            </label>
            <label>
              Role
              <select
                value={userForm.role}
                onChange={(e) =>
                  setUserForm({ ...userForm, role: e.target.value })
                }
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                {user.role === "super_admin" && (
                  <option value="super_admin">Super admin</option>
                )}
              </select>
            </label>
            {editingUser && Number(editingUser.id) !== Number(user.id) && (
              <label className="check span2">
                <input
                  type="checkbox"
                  checked={userForm.isActive}
                  onChange={(e) =>
                    setUserForm({ ...userForm, isActive: e.target.checked })
                  }
                />
                <span>
                  <b>User can sign in</b>
                  <small>
                    Uncheck to suspend access without deleting financial data.
                  </small>
                </span>
              </label>
            )}
            <button className="primary span2 modal-submit">
              {editingUser ? "Save user" : "Create user"}
            </button>
          </form>
        </Modal>
      )}
      {modal === "localization" && (
        <Modal
          title={t(user.locale, "localization")}
          screenId="D0804"
          onClose={() => setModal(null)}
        >
          <div className="localization-toolbar">
            <input
              value={translationSearch}
              onChange={(e) => setTranslationSearch(e.target.value)}
              placeholder="Search strings…"
            />
            <button
              type="button"
              className={editEnglish ? "primary compact" : "secondary compact"}
              onClick={() => setEditEnglish(!editEnglish)}
            >
              {t(user.locale, editEnglish ? "lockEnglish" : "editEnglish")}
            </button>
          </div>
          <div className="table-wrap localization-table-wrap">
            <table className="localization-table">
              <thead><tr><th>{t(user.locale, "translationKey")}</th><th>English</th><th>Español</th><th>Português</th></tr></thead>
              <tbody>
                {localizationRows
                  .filter((row) => !translationSearch || Object.values(row).some((v) => String(v).toLowerCase().includes(translationSearch.toLowerCase())))
                  .map((row) => (
                    <tr key={row.key}>
                      <td><code>{row.key}</code></td>
                      {['en','es','pt'].map((lang) => (
                        <td key={lang}>
                          <textarea
                            value={row[lang]}
                            disabled={lang === 'en' && !editEnglish}
                            onChange={(e) => setLocalizationRows((all) => all.map((x) => x.key === row.key ? { ...x, [lang]: e.target.value } : x))}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <button className="primary full" onClick={saveLocalization}>
            {t(user.locale, "saveTranslations")}
          </button>
        </Modal>
      )}
    </>
  );
}
